import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Highlight {{tokens}} with a colored background (no padding/margin so caret stays aligned).
const highlight = (text) =>
  escapeHtml(text || "").replace(
    /\{\{\s*[a-zA-Z0-9_.]+\s*\}\}/g,
    (m) => `<mark style="background:#dbeafe;color:#1d4ed8;border-radius:3px;">${m}</mark>`
  ) + "\n";

export function MergeFieldEditor({ value, onChange, fields = [], rows = 12, placeholder, testid = "merge-field-editor" }) {
  const taRef = useRef(null);
  const backRef = useRef(null);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const syncScroll = () => {
    if (backRef.current && taRef.current) {
      backRef.current.scrollTop = taRef.current.scrollTop;
      backRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const insertToken = (token) => {
    const ta = taRef.current;
    const text = value || "";
    const start = ta ? ta.selectionStart : text.length;
    const end = ta ? ta.selectionEnd : text.length;
    const chunk = `{{${token}}}`;
    const next = text.slice(0, start) + chunk + text.slice(end);
    onChange(next);
    setOpen(false); setSearch("");
    requestAnimationFrame(() => {
      if (ta) { ta.focus(); const pos = start + chunk.length; ta.setSelectionRange(pos, pos); syncScroll(); }
    });
  };

  const filtered = fields
    .map((g) => ({ ...g, fields: g.fields.filter((f) => {
      const q = search.trim().toLowerCase();
      return !q || f.label.toLowerCase().includes(q) || f.token.toLowerCase().includes(q);
    }) }))
    .filter((g) => g.fields.length > 0);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-500">Template Content</span>
        <div className="relative">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-insert-btn`}
            className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-2.5 py-1.5 hover:bg-blue-100 transition-colors">
            <Icons.Braces className="h-3.5 w-3.5" />Insert Merge Field<Icons.ChevronDown className="h-3.5 w-3.5" />
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 w-72 max-h-80 overflow-auto bg-white border border-slate-200 rounded-xl shadow-lg z-30 p-2" data-testid={`${testid}-field-menu`}>
              <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-2 py-1.5 mb-2 sticky top-0 bg-white">
                <Icons.Search className="h-3.5 w-3.5 text-slate-400" />
                <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fields…"
                  data-testid={`${testid}-search`} className="flex-1 text-xs outline-none" />
              </div>
              {filtered.length === 0 && <p className="text-xs text-slate-400 px-2 py-3">No matching fields.</p>}
              {filtered.map((g) => (
                <div key={g.category} className="mb-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-1.5 mb-1">{g.category}</div>
                  <div className="flex flex-wrap gap-1">
                    {g.fields.map((f) => (
                      <button key={f.token} type="button" onClick={() => insertToken(f.token)}
                        data-testid={`${testid}-field-${f.token}`} title={`{{${f.token}}}`}
                        className="text-[11px] bg-slate-50 hover:bg-blue-50 hover:text-blue-700 text-slate-600 border border-slate-200 rounded px-1.5 py-1 transition-colors">
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="relative border border-slate-300 rounded-lg overflow-hidden" style={{ minHeight: `${rows * 1.6}em` }}>
        <div ref={backRef} aria-hidden="true"
          className="absolute inset-0 px-3 py-2 text-sm font-mono leading-relaxed whitespace-pre-wrap break-words overflow-auto pointer-events-none text-slate-800"
          dangerouslySetInnerHTML={{ __html: highlight(value) }} />
        <textarea ref={taRef} value={value || ""} onChange={(e) => onChange(e.target.value)} onScroll={syncScroll}
          rows={rows} placeholder={placeholder} spellCheck={false} data-testid={`${testid}-textarea`}
          className="relative w-full px-3 py-2 text-sm font-mono leading-relaxed whitespace-pre-wrap break-words bg-transparent text-transparent caret-slate-900 resize-y outline-none" />
      </div>
    </div>
  );
}

export default MergeFieldEditor;
